import moment from 'moment'
import React, { FC, useCallback, useEffect, useRef, useState } from 'react'

import { EuiButton, EuiDatePicker, EuiFieldNumber, EuiFieldText, EuiFlexGroup, EuiFlexItem, EuiFlyoutBody, EuiFlyoutFooter, EuiFlyoutHeader, EuiForm, EuiFormRow, EuiRadio, EuiRadioGroup, EuiSelect, EuiSpacer, EuiSwitch, EuiText, EuiTextArea, EuiTitle } from '@elastic/eui'
import AcFlyout from 'components/AcFlyout'

import { AIEditTargetType } from 'api/interfaces/AIEditTypes'
import { MasterCampaignPlanCampaignBuilderType } from 'api/interfaces/MasterCampaignStub'
import BlueprintEditWarning from 'features/planner/components/BlueprintEditWarning'
import { getCampaignTypeNameLong } from 'utils/CampaignType'

export interface CampaignConstraints {
  campaignType?: string
  startDate?: string
  duration?: string
  budget?: string
}

const addCampaignPlaceholders: Record<string, string> = {
  TV: 'e.g., Target young families in the Nashville metro area.',
  Internet: 'e.g., Target young professionals interested in fitness in Austin, TX.',
  Billboard: 'e.g., Focus on high-traffic areas near downtown Atlanta.'
}

const segmentPlaceholders: Record<string, string> = {
  TV: 'e.g., Focus on households with children and higher income brackets.',
  Internet: 'e.g., Add in-market segments for home improvement and refine age ranges.',
  Billboard: 'e.g., Broaden the audience to include commuters aged 25-54.'
}

const getPromptDefaults = (targetType: AIEditTargetType, targetLabel: string, campaignType?: string): { label: string; placeholder: string } => {
  switch (targetType) {
    case 'AddCampaign':
      return {
        label: 'Describe the campaign you want AdCritter AI to create',
        placeholder: addCampaignPlaceholders[campaignType ?? ''] || addCampaignPlaceholders.Internet
      }
    case 'EditCampaign':
      return {
        label: 'Describe how you want AdCritter AI to edit this campaign',
        placeholder: 'e.g., Lengthen the campaign by 2 months and use fewer zipcodes.'
      }
    case 'AddPersona':
      return {
        label: 'Describe the new persona you want AdCritter AI to create',
        placeholder: 'e.g., A 35-45 year old suburban homeowner who values quality over price and researches extensively online before purchasing.'
      }
    case 'Persona':
      return {
        label: 'What changes should AdCritter AI make to this persona?',
        placeholder: 'e.g., Make this persona slightly older with a wider income range.'
      }
    case 'MarketAnalysisSection':
    case 'MarketAnalysisField':
    case 'MarketAnalysisListField':
      return {
        label: `What changes should AdCritter AI make to ${targetLabel}?`,
        placeholder: 'e.g., Make this section 50% longer.'
      }
    case 'CampaignSummary':
      return {
        label: `What changes should AdCritter AI make to ${targetLabel}?`,
        placeholder: 'e.g., Update the summary to reflect recent campaign changes.'
      }
    case 'CampaignTiming':
      return {
        label: `What changes should AdCritter AI make to ${targetLabel}?`,
        placeholder: 'e.g., Update timing to reflect the new start date and duration.'
      }
    case 'CampaignGeography':
      return {
        label: `What changes should AdCritter AI make to ${targetLabel}?`,
        placeholder: 'e.g., Add more zip codes in the Nashville metro area.'
      }
    case 'CampaignSegments':
      return {
        label: `What changes should AdCritter AI make to ${targetLabel}?`,
        placeholder: segmentPlaceholders[campaignType ?? ''] || segmentPlaceholders.Internet
      }
    case 'ExecSummaryIntroduction':
    case 'ExecSummaryChannelUtilization':
    case 'ExecSummaryAudienceStrategy':
    case 'ExecSummaryGeographicTargeting':
      return {
        label: `What changes should AdCritter AI make to ${targetLabel}?`,
        placeholder: 'e.g., Make this section 50% longer.'
      }
    default:
      return {
        label: 'What changes should AdCritter AI make?',
        placeholder: 'e.g., Describe the changes you would like AdCritter AI to make.'
      }
  }
}

export interface AIEditPromptContentProps {
  targetType?: AIEditTargetType
  targetLabel: string
  onSubmit: (prompt: string, constraints?: CampaignConstraints, deepThink?: boolean) => void
  isSubmitting?: boolean
  showBlueprintWarning?: boolean
  promptLabel?: string
  promptPlaceholder?: string
  showConstraints?: boolean
  initialConstraints?: CampaignConstraints
  hideChannelType?: boolean
  campaignType?: string
}

interface AIEditPromptFlyoutProps extends AIEditPromptContentProps {
  onClose: () => void
  onBack?: () => void
}

const campaignTypeOptions = [
  { value: '', text: 'AI chooses' },
  { value: 'TV', text: 'TV' },
  { value: 'Billboard', text: 'Billboard' },
  { value: 'Internet', text: 'Internet' }
]

export const AIEditPromptContent: FC<AIEditPromptContentProps & { deepThink?: boolean; onSubmitRef?: React.MutableRefObject<(() => void) | null>; onSubmitDisabledChange?: (disabled: boolean) => void }> = ({ targetType, targetLabel, onSubmit, isSubmitting = false, showBlueprintWarning = false, promptLabel, promptPlaceholder, showConstraints = false, initialConstraints, hideChannelType = false, campaignType: campaignTypeProp, deepThink = false, onSubmitRef, onSubmitDisabledChange }) => {
  const isEditMode = !!initialConstraints
  const defaults = targetType ? getPromptDefaults(targetType, targetLabel, campaignTypeProp) : undefined
  const resolvedPromptLabel = promptLabel || defaults?.label || 'What changes should AdCritter AI make?'
  const resolvedPromptPlaceholder = promptPlaceholder || defaults?.placeholder || ''
  const isPromptOptional = targetType === 'AddCampaign'

  const [prompt, setPrompt] = useState('')
  const [campaignType, setCampaignType] = useState(initialConstraints?.campaignType ?? 'TV')
  const [startDate, setStartDate] = useState(initialConstraints?.startDate ?? '')
  const [duration, setDuration] = useState(initialConstraints?.duration ?? '')
  const [budget, setBudget] = useState(initialConstraints?.budget ?? '')

  const [useCustomCampaignType, setUseCustomCampaignType] = useState(false)
  const [useCustomStartDate, setUseCustomStartDate] = useState(false)
  const [useCustomDuration, setUseCustomDuration] = useState(false)
  const [useCustomBudget, setUseCustomBudget] = useState(false)

  const handleSubmit = () => {
    if (!isPromptOptional && !prompt.trim()) return

    if (!showConstraints) {
      onSubmit(prompt.trim(), undefined, deepThink)
      return
    }

    if (isEditMode) {
      onSubmit(prompt.trim(), {
        campaignType: campaignType || undefined,
        startDate: startDate || undefined,
        duration: duration || undefined,
        budget: budget || undefined
      }, deepThink)
    } else {
      onSubmit(prompt.trim(), {
        campaignType: useCustomCampaignType ? campaignType : undefined,
        startDate: useCustomStartDate ? startDate : undefined,
        duration: useCustomDuration ? duration : undefined,
        budget: useCustomBudget ? budget : undefined
      }, deepThink)
    }
  }

  const isSubmitDisabled = (!isPromptOptional && !prompt.trim()) || !!isSubmitting

  if (onSubmitRef) {
    onSubmitRef.current = handleSubmit
  }

  useEffect(() => {
    onSubmitDisabledChange?.(isSubmitDisabled)
  }, [isSubmitDisabled, onSubmitDisabledChange])

  const renderEditConstraints = () => (
    <>
      <EuiSpacer size='l' />
      <EuiText size='s'>
        <strong>Campaign Parameters</strong>
        <p>Modify values to change the campaign's business fields, or leave as-is.</p>
      </EuiText>
      <EuiSpacer size='s' />
      <EuiFlexGroup gutterSize='m'>
        <EuiFlexItem>
          <EuiFormRow label='Campaign Type'>
            <EuiSelect options={campaignTypeOptions.filter(o => o.value !== '')} value={campaignType} onChange={e => setCampaignType(e.target.value)} disabled={isSubmitting} />
          </EuiFormRow>
        </EuiFlexItem>
        <EuiFlexItem>
          <EuiFormRow label='Start Date'>
            <EuiFieldText type='date' value={startDate} onChange={e => setStartDate(e.target.value)} disabled={isSubmitting} />
          </EuiFormRow>
        </EuiFlexItem>
      </EuiFlexGroup>
      <EuiSpacer size='s' />
      <EuiFlexGroup gutterSize='m'>
        <EuiFlexItem>
          <EuiFormRow label='Duration (months)'>
            <EuiFieldNumber min={1} max={24} value={duration} onChange={e => setDuration(e.target.value)} disabled={isSubmitting} />
          </EuiFormRow>
        </EuiFlexItem>
        <EuiFlexItem>
          <EuiFormRow label='Monthly Budget ($)'>
            <EuiFieldNumber min={0} value={budget} onChange={e => setBudget(e.target.value)} disabled={isSubmitting} />
          </EuiFormRow>
        </EuiFlexItem>
      </EuiFlexGroup>
    </>
  )

  const renderAddConstraints = () => (
    <>
      {!hideChannelType && (
        <>
          <EuiTitle size='s'>
            <h2>Channel Type *</h2>
          </EuiTitle>
          <EuiText>Select the advertising channel for this campaign.</EuiText>
          <EuiSpacer />
          <EuiRadio id='campaignType_ai' label='AI Recommended' checked={!useCustomCampaignType} onChange={() => setUseCustomCampaignType(false)} disabled={isSubmitting} />
          <EuiRadio id='campaignType_custom' label='Custom' checked={useCustomCampaignType} onChange={() => setUseCustomCampaignType(true)} disabled={isSubmitting} />
          <div style={{ marginLeft: 24, marginTop: 4 }}>
            <EuiForm>
              <EuiFormRow>
                <EuiRadioGroup
                  options={[
                    { id: MasterCampaignPlanCampaignBuilderType.TV, label: getCampaignTypeNameLong(MasterCampaignPlanCampaignBuilderType.TV) },
                    { id: MasterCampaignPlanCampaignBuilderType.Billboard, label: getCampaignTypeNameLong(MasterCampaignPlanCampaignBuilderType.Billboard) },
                    { id: MasterCampaignPlanCampaignBuilderType.Audio, label: getCampaignTypeNameLong(MasterCampaignPlanCampaignBuilderType.Audio) },
                    { id: MasterCampaignPlanCampaignBuilderType.Internet, label: getCampaignTypeNameLong(MasterCampaignPlanCampaignBuilderType.Internet) }
                  ]}
                  idSelected={useCustomCampaignType ? campaignType : ''}
                  onChange={id => {
                    setCampaignType(id)
                    setUseCustomCampaignType(true)
                  }}
                  disabled={!useCustomCampaignType || isSubmitting}
                />
              </EuiFormRow>
            </EuiForm>
          </div>

          <EuiSpacer size='l' />
        </>
      )}

      <EuiTitle size='s'>
        <h2>Budgeting *</h2>
      </EuiTitle>
      <EuiText>Set the monthly budget for this campaign.</EuiText>
      <EuiSpacer />
      <EuiRadio id='budget_ai' label='AI Recommended' checked={!useCustomBudget} onChange={() => setUseCustomBudget(false)} disabled={isSubmitting} />
      <EuiRadio id='budget_custom' label='Custom' checked={useCustomBudget} onChange={() => setUseCustomBudget(true)} disabled={isSubmitting} />
      <div style={{ marginLeft: 24, marginTop: 4 }}>
        <EuiForm>
          <EuiFormRow label='Monthly Budget'>
            <EuiFieldNumber
              prepend='$'
              min={100}
              value={budget}
              onChange={e => {
                setBudget(e.target.value)
                setUseCustomBudget(true)
              }}
              disabled={!useCustomBudget || isSubmitting}
            />
          </EuiFormRow>
        </EuiForm>
      </div>

      <EuiSpacer size='l' />

      <EuiTitle size='s'>
        <h2>Timeframe *</h2>
      </EuiTitle>
      <EuiText>Set the start date and duration for this campaign.</EuiText>
      <EuiSpacer />

      <EuiText size='s'>
        <strong>Start Date</strong>
      </EuiText>
      <EuiSpacer size='xs' />
      <EuiRadio id='startDate_ai' label='AI Recommended' checked={!useCustomStartDate} onChange={() => setUseCustomStartDate(false)} disabled={isSubmitting} />
      <EuiRadio id='startDate_custom' label='Custom' checked={useCustomStartDate} onChange={() => setUseCustomStartDate(true)} disabled={isSubmitting} />
      <div style={{ marginLeft: 24, marginTop: 4 }}>
        <EuiForm>
          <EuiFormRow label='Start Date'>
            <EuiDatePicker
              selected={startDate ? moment(startDate) : undefined}
              onChange={date => {
                setStartDate(date ? date.format('YYYY-MM-DD') : '')
                setUseCustomStartDate(true)
              }}
              dateFormat='MM/DD/yyyy'
              minDate={moment().add(3, 'days')}
              disabled={!useCustomStartDate || isSubmitting}
            />
          </EuiFormRow>
        </EuiForm>
      </div>

      <EuiSpacer size='m' />

      <EuiText size='s'>
        <strong>Duration</strong>
      </EuiText>
      <EuiSpacer size='xs' />
      <EuiRadio id='duration_ai' label='AI Recommended' checked={!useCustomDuration} onChange={() => setUseCustomDuration(false)} disabled={isSubmitting} />
      <EuiRadio id='duration_custom' label='Custom' checked={useCustomDuration} onChange={() => setUseCustomDuration(true)} disabled={isSubmitting} />
      <div style={{ marginLeft: 24, marginTop: 4 }}>
        <EuiForm>
          <EuiFormRow label='Duration (Months)'>
            <EuiFieldNumber
              min={1}
              max={24}
              value={duration}
              onChange={e => {
                setDuration(e.target.value)
                setUseCustomDuration(true)
              }}
              disabled={!useCustomDuration || isSubmitting}
            />
          </EuiFormRow>
        </EuiForm>
      </div>
    </>
  )

  const renderPrompt = () => (
    <>
      <EuiTitle size='s'>
        <h2>{resolvedPromptLabel}</h2>
      </EuiTitle>
      {isPromptOptional && <EuiText>Optional additional guidance for the AI.</EuiText>}
      <EuiSpacer size='s' />
      <EuiTextArea fullWidth rows={showConstraints ? 6 : 15} value={prompt} onChange={e => setPrompt(e.target.value)} placeholder={resolvedPromptPlaceholder} disabled={isSubmitting} />
    </>
  )

  return (
    <>
      <BlueprintEditWarning show={showBlueprintWarning} />

      {showConstraints && !isEditMode && (
        <>
          {renderAddConstraints()}
          <EuiSpacer size='l' />
        </>
      )}

      {showConstraints && isEditMode && (
        <>
          {renderEditConstraints()}
          <EuiSpacer size='xxl' />
        </>
      )}

      {renderPrompt()}
    </>
  )
}

const complexEditTargetTypes: AIEditTargetType[] = ['EditCampaign', 'AddCampaign', 'CampaignSegments', 'CampaignAudience']

const AIEditPromptFlyout: FC<AIEditPromptFlyoutProps> = ({ onClose, onBack, isSubmitting = false, ...contentProps }) => {
  const submitRef = useRef<(() => void) | null>(null)
  const [isSubmitDisabled, setIsSubmitDisabled] = useState(true)
  const [deepThink, setDeepThink] = useState(false)
  const handleSubmitDisabledChange = useCallback((disabled: boolean) => setIsSubmitDisabled(disabled), [])
  const showComplexEditBanner = isSubmitting && (deepThink || (contentProps.targetType && complexEditTargetTypes.includes(contentProps.targetType)))

  return (
    <AcFlyout onClose={onClose} size='m' ownFocus>
      <EuiFlyoutHeader hasBorder>
        <EuiTitle size='m'>
          <h2>{contentProps.targetLabel}</h2>
        </EuiTitle>
      </EuiFlyoutHeader>

      <EuiFlyoutBody>
        <AIEditPromptContent {...contentProps} deepThink={deepThink} isSubmitting={isSubmitting} onSubmitRef={submitRef} onSubmitDisabledChange={handleSubmitDisabledChange} />
      </EuiFlyoutBody>

      <EuiFlyoutFooter>
        <EuiFlexGroup direction='row' alignItems='center'>
          <EuiFlexItem grow={false}>
            <EuiButton onClick={onBack || onClose} disabled={isSubmitting}>
              {onBack ? 'Back' : 'Cancel'}
            </EuiButton>
          </EuiFlexItem>
          <EuiFlexItem grow={false}>
            <EuiButton onClick={() => submitRef.current?.()} fill disabled={isSubmitDisabled} isLoading={isSubmitting}>
              Submit
            </EuiButton>
          </EuiFlexItem>
          <EuiFlexItem grow={false}>
            <EuiSwitch label='Deep Think' checked={deepThink} onChange={e => setDeepThink(e.target.checked)} disabled={isSubmitting} compressed />
          </EuiFlexItem>
          {showComplexEditBanner && (
            <EuiFlexItem grow={false}>
              <EuiText size='xs' style={{ backgroundColor: '#D3DAE6', borderRadius: 4, padding: '4px 8px' }}>
                Complex edit. Allow 30 seconds to 2 minutes.
              </EuiText>
            </EuiFlexItem>
          )}
        </EuiFlexGroup>
      </EuiFlyoutFooter>
    </AcFlyout>
  )
}

export default AIEditPromptFlyout
